//////////////////////////////////////////////////////////////////////
//
// These types must be kept in sync with the backend API types
// (rust/server/src/api/structs.rs).
//
import { useEffect, useState } from 'react';

import useSWR, { mutate } from 'swr';

import { API_URL } from './consts';
import { DISPLAY_IMAGE_CDN, type DisplayImage } from './displayImageContract';
import type { OAuthIdentityInfo, OAuthProvider } from './oauth';
import { RegistrationStatus } from './registrationContract';

export { buildDocThreadView } from './docThreadView';
export type { DocThreadView } from './docThreadView';

export type PublicDocId = string & { readonly __brand: 'PublicDocId' };

export function asPublicDocId(value: string): PublicDocId {
  return value as PublicDocId;
}

/**
 * Stable public identifier for a user account, assigned by the backend.
 * Must be kept in sync with PublicUserId in rust/server/src/api/public_id.rs.
 */
export type PublicUserId = string & { readonly __brand: 'PublicUserId' };

export function asPublicUserId(value: string): PublicUserId {
  return value as PublicUserId;
}

/**
 * Runtime client error identifiers returned by server error middleware.
 * Must be kept in sync with ClientError in rust/server/src/error.rs.
 */
export const CLIENT_ERROR_CODE = {
  NoAuth: 'no_auth',
  ServiceError: 'service_error',
  RegFail: 'reg_fail',
  RequestRejected: 'request_rejected',
  InvalidRequestData: 'invalid_request_data',
  OAuthCodeAlreadyUsed: 'oauth_code_already_used',
  Forbidden: 'forbidden',
  UserNotFound: 'user_not_found',
  ProductNotFound: 'product_not_found',
  InvalidProductReference: 'invalid_product_reference',
  RegistrationLoginFailed: 'registration_login_failed',
  DocumentNotFound: 'document_not_found',
} as const;
/** Union of client error identifiers returned by server error middleware. */
export type ClientErrorCode = (typeof CLIENT_ERROR_CODE)[keyof typeof CLIENT_ERROR_CODE];

/** Must be kept in sync with `ApiErrorBody` in rust/server/src/error.rs. */
export type ApiErrorBody = { req_uuid: string } & (
  | { type: typeof CLIENT_ERROR_CODE.InvalidRequestData; reason: RequestError }
  | { type: typeof CLIENT_ERROR_CODE.RequestRejected; reason: RequestRejection }
  | {
      type: Exclude<
        ClientErrorCode,
        typeof CLIENT_ERROR_CODE.InvalidRequestData | typeof CLIENT_ERROR_CODE.RequestRejected
      >;
    }
);

/** Safe HTTP rejection reasons; keep in sync with RequestRejection in rust/server/src/error.rs. */
export const REQUEST_REJECTION = {
  MalformedRequest: 'malformed_request',
  RouteNotFound: 'route_not_found',
  MethodNotAllowed: 'method_not_allowed',
  PayloadTooLarge: 'payload_too_large',
  UriTooLong: 'uri_too_long',
  UnsupportedMediaType: 'unsupported_media_type',
  UnprocessableEntity: 'unprocessable_entity',
  TooManyRequests: 'too_many_requests',
  Other: 'other',
} as const;
type RequestRejection = (typeof REQUEST_REJECTION)[keyof typeof REQUEST_REJECTION];

/** Safe request validation reasons; keep in sync with RequestError in rust/server/src/error.rs. */
export const REQUEST_ERROR = {
  OnlyAuthenticationMethod: 'only_authentication_method',
  OAuthIdentityNotFound: 'oauth_identity_not_found',
  InvalidUrl: 'invalid_url',
  UnsupportedMarketplace: 'unsupported_marketplace',
  ProductIdMissing: 'product_id_missing',
  SearchQueryTooLong: 'search_query_too_long',
  InvalidInviteCreditQuantity: 'invalid_invite_credit_quantity',
  InvalidConceptReference: 'invalid_concept_reference',
  JustificationTooLong: 'justification_too_long',
  InvalidJudgmentSubject: 'invalid_judgment_subject',
  InvalidJudgmentRelation: 'invalid_judgment_relation',
  InvalidJudgmentObject: 'invalid_judgment_object',
  ReflexiveAssertion: 'reflexive_assertion',
  UnsupportedRelation: 'unsupported_relation',
  InvalidQuality: 'invalid_quality',
  EmptyJudgments: 'empty_judgments',
  TooManyJudgments: 'too_many_judgments',
  DuplicateJudgment: 'duplicate_judgment',
  InvalidJudgmentRequest: 'invalid_judgment_request',
} as const;
type RequestError = (typeof REQUEST_ERROR)[keyof typeof REQUEST_ERROR];

/** UI text for actionable validation failures previously described by backend strings. */
const REQUEST_ERROR_MESSAGES: Record<RequestError, string> = {
  [REQUEST_ERROR.OnlyAuthenticationMethod]:
    'Cannot remove your only authentication method. Please set a password first.',
  [REQUEST_ERROR.OAuthIdentityNotFound]: 'OAuth identity not found.',
  [REQUEST_ERROR.InvalidUrl]: 'Invalid URL format.',
  [REQUEST_ERROR.UnsupportedMarketplace]: 'This marketplace is not supported.',
  [REQUEST_ERROR.ProductIdMissing]: 'Could not extract a product ID from the URL.',
  [REQUEST_ERROR.SearchQueryTooLong]: 'Search query is too long.',
  [REQUEST_ERROR.InvalidInviteCreditQuantity]: 'Invitation credit quantity must be positive.',
  [REQUEST_ERROR.InvalidConceptReference]: 'Invalid concept reference.',
  [REQUEST_ERROR.JustificationTooLong]: 'Judgment justification is too long.',
  [REQUEST_ERROR.InvalidJudgmentSubject]: 'Judgment subject is not a valid concept reference.',
  [REQUEST_ERROR.InvalidJudgmentRelation]: 'Judgment relation is not a valid concept reference.',
  [REQUEST_ERROR.InvalidJudgmentObject]: 'Judgment object is not a valid concept reference.',
  [REQUEST_ERROR.ReflexiveAssertion]: 'Reflexive assertions are not allowed.',
  [REQUEST_ERROR.UnsupportedRelation]: 'Judgment relation is not supported.',
  [REQUEST_ERROR.InvalidQuality]: 'Concept quality is not a canonical quality value.',
  [REQUEST_ERROR.EmptyJudgments]: 'At least one judgment is required.',
  [REQUEST_ERROR.TooManyJudgments]: 'Too many judgments in one commit.',
  [REQUEST_ERROR.DuplicateJudgment]: 'Duplicate literal judgment triple.',
  [REQUEST_ERROR.InvalidJudgmentRequest]: 'Invalid admin schema judgment request.',
};

/** Must be kept in sync with `ApiErrorEnvelope` in rust/server/src/error.rs. */
export interface ApiErrorEnvelope {
  error: ApiErrorBody;
}

const CLIENT_ERROR_CODES = new Set<ClientErrorCode>(Object.values(CLIENT_ERROR_CODE));
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Safely narrows an untrusted server response to the shared code-only error contract. */
export function parseApiErrorEnvelope(value: unknown): ApiErrorEnvelope | null {
  if (typeof value !== 'object' || value === null || !('error' in value)) return null;
  if (Object.keys(value).length !== 1) return null;

  const error = value.error;
  if (typeof error !== 'object' || error === null) return null;

  const candidate = error as Record<string, unknown>;
  if (
    typeof candidate.type !== 'string' ||
    !CLIENT_ERROR_CODES.has(candidate.type as ClientErrorCode) ||
    typeof candidate.req_uuid !== 'string' ||
    !UUID_PATTERN.test(candidate.req_uuid)
  ) {
    return null;
  }

  if (candidate.type === CLIENT_ERROR_CODE.InvalidRequestData) {
    if (
      Object.keys(candidate).length !== 3 ||
      !Object.values(REQUEST_ERROR).includes(candidate.reason as RequestError)
    )
      return null;
    return {
      error: {
        type: candidate.type,
        reason: candidate.reason as RequestError,
        req_uuid: candidate.req_uuid,
      },
    };
  }
  if (candidate.type === CLIENT_ERROR_CODE.RequestRejected) {
    if (
      Object.keys(candidate).length !== 3 ||
      !Object.values(REQUEST_REJECTION).includes(candidate.reason as RequestRejection)
    )
      return null;
    return {
      error: {
        type: candidate.type,
        reason: candidate.reason as RequestRejection,
        req_uuid: candidate.req_uuid,
      },
    };
  }
  if (Object.keys(candidate).length !== 2) return null;

  return {
    error: {
      type: candidate.type as Exclude<
        ClientErrorCode,
        typeof CLIENT_ERROR_CODE.InvalidRequestData | typeof CLIENT_ERROR_CODE.RequestRejected
      >,
      req_uuid: candidate.req_uuid,
    },
  };
}

/** Reads the shared error envelope without exposing arbitrary response content. */
export async function parseApiErrorResponse(response: Response): Promise<ApiErrorEnvelope | null> {
  try {
    return parseApiErrorEnvelope(await response.json());
  } catch {
    return null;
  }
}

/** @public Must stay in sync with `SuccessStatus` in rust/server/src/api/structs.rs. */
export const SUCCESS_STATUS = {
  Success: 'success',
} as const;
/** @public Successful acknowledgement status shared by simple API responses. */
export type SuccessStatus = (typeof SUCCESS_STATUS)[keyof typeof SUCCESS_STATUS];

/** @public Must stay in sync with `SuccessResponse` in rust/server/src/api/structs.rs. */
export interface SuccessResponse {
  status: SuccessStatus;
}

/** Must stay in sync with `AuthCheckResponse` in rust/server/src/api/structs.rs. */
export interface AuthCheckResponse {
  valid: boolean;
}

type ApiErrorContext =
  | { operation: 'login' }
  | { operation: 'password_change' }
  | { operation: 'oauth_removal'; provider: OAuthProvider }
  | { operation: 'registration' }
  | { operation: 'username_check' };

type StaticApiErrorOperation = Exclude<ApiErrorContext['operation'], 'oauth_removal'>;

const API_ERROR_MESSAGES: Record<StaticApiErrorOperation, string> = {
  login: 'Login failed. Please check your credentials and try again.',
  password_change: 'Failed to change password.',
  registration: 'Registration failed. Please try again.',
  username_check: 'Failed to check username availability.',
};

/** Maps status and machine code to context-owned user-facing text. */
export function apiErrorMessage(
  context: ApiErrorContext,
  status: number,
  envelope: ApiErrorEnvelope | null
): string {
  if (envelope?.error.type === CLIENT_ERROR_CODE.InvalidRequestData) {
    return REQUEST_ERROR_MESSAGES[envelope.error.reason];
  }
  if (envelope?.error.type === CLIENT_ERROR_CODE.RegistrationLoginFailed) {
    return 'Your account was created, but automatic sign-in failed. Please sign in.';
  }
  if (context.operation === 'login' && (status === 401 || status === 403)) {
    return API_ERROR_MESSAGES.login;
  }
  if (context.operation === 'password_change' && status === 403) {
    return 'Your current password could not be verified.';
  }
  if (envelope?.error.type === CLIENT_ERROR_CODE.NoAuth) {
    return 'Your session has expired. Please log in and try again.';
  }
  if (context.operation === 'oauth_removal') {
    return `Failed to remove the ${context.provider} connection.`;
  }
  return API_ERROR_MESSAGES[context.operation];
}

/** @public Must be kept in sync with `HealthCheckResponse` in rust/server/src/api/structs.rs. */
export interface HealthCheckResponse {
  status: string;
  version: string;
  source_dirty: boolean;
  environment: string;
}

/**
 * Runtime document visibility identifiers shared with the backend contract.
 * Must be kept in sync with rust/server/src/api/structs.rs.
 */
export const DOC_VISIBILITY = {
  Public: 'public',
  Friends: 'friends',
  Private: 'private',
} as const;
export type DocVisibility = (typeof DOC_VISIBILITY)[keyof typeof DOC_VISIBILITY];

/** Enumerates document visibility values in menu and validation order. */
export const DOC_VISIBILITY_VALUES = [
  DOC_VISIBILITY.Public,
  DOC_VISIBILITY.Friends,
  DOC_VISIBILITY.Private,
] as const satisfies readonly DocVisibility[];

/** Assigns a numeric restrictiveness rank to each document visibility. */
export const DOC_VISIBILITY_ORDER: Record<DocVisibility, number> = {
  [DOC_VISIBILITY.Public]: 0,
  [DOC_VISIBILITY.Friends]: 1,
  [DOC_VISIBILITY.Private]: 2,
};

/**
 * Runtime follow action identifiers shared with the backend contract.
 * Must be kept in sync with rust/server/src/api/structs.rs.
 */
export const UPDATE_FOLLOW_ACTION = {
  Follow: 'follow',
  Unfollow: 'unfollow',
  Update: 'update',
} as const;
export type UpdateFollowAction = (typeof UPDATE_FOLLOW_ACTION)[keyof typeof UPDATE_FOLLOW_ACTION];

/**
 * Runtime collection action identifiers shared with the backend contract.
 * Must be kept in sync with rust/server/src/api/structs.rs.
 */
export const UPDATE_COLLECTIONS_ACTION = {
  Add: 'add',
  Remove: 'remove',
} as const;
export type UpdateCollectionsAction =
  (typeof UPDATE_COLLECTIONS_ACTION)[keyof typeof UPDATE_COLLECTIONS_ACTION];

// Vote action: what the user explicitly did
// IMPORTANT: Must be kept in sync with backend VoteAction enum
// in rust/base/src/kb/vote/mod.rs
export type VoteAction = 'upvote' | 'downvote' | 'remove';

// Vote state: the current state of a user's vote
// IMPORTANT: Must be kept in sync with backend VoteState enum
// in rust/base/src/kb/vote/mod.rs
export type VoteState = 'up' | 'down';

// Vote info safe for any authenticated user's API response
// IMPORTANT: Must be kept in sync with backend DisclosedVoteInfo struct
// in rust/base/src/kb/vote/mod.rs
export interface DisclosedVoteInfo {
  score: number;
  user_vote: VoteState | null;
}

// Background task statuses used by the doc live-update websocket.
// IMPORTANT: Must be kept in sync with rust/work/src/bg/mod.rs BgTaskUpdate.
export enum BgTaskStatus {
  Reading = 'Reading',
  ReadingThread = 'ReadingThread',
  Abstain = 'Abstain',
  Researching = 'Researching',
  WebSearching = 'WebSearching',
  WebFetching = 'WebFetching',
  SearchingOffers = 'SearchingOffers',
  AnalyzingSearchResults = 'AnalyzingSearchResults',
  FetchingOfferDetails = 'FetchingOfferDetails',
  FetchingReviews = 'FetchingReviews',
  LoadingSiteInfo = 'LoadingSiteInfo',
  ReformulatingQuery = 'ReformulatingQuery',
  ReformulatingOfferTitle = 'ReformulatingOfferTitle',
  Recommending = 'Recommending',
  Replying = 'Replying',
  Answered = 'Answered',
  Replied = 'Replied',
  Completed = 'Completed',
  Failed = 'Failed',
  WritingGuides = 'WritingGuides',
}

export { DISPLAY_IMAGE_CDN, type DisplayImage };

// A concept reference, which is a unique identifier for a concept.  Used
// for both general concepts (topics) and specific offers.
export interface DisplayConceptRef {
  cref: string;
  display: string;
  description: string; // Contains descriptive text (formerly overview)
  mention?: string; // For when this concept is mentioned in text
  preview_image?: DisplayImage; // Preview image for this concept if available
  is_offer: boolean; // Indicates if this concept is an offer (SKU)
}

/** Namespace prefix identifying offer concept references. */
export const OFFER_NAMESPACE = '/sku/';

// TODO(1): use consistently; there are places in the code base where ad-hoc
// structures are used instead of this.
export interface DisplayConceptDef {
  key: string;
  display_name: string;
  description: string;
  sup: string[];
  sub: string[];
}

/** @public Names shared by the backend's internal recon classification and response buckets. */
export const RECON_RELATION = {
  Disjoint: 'disjoint',
  Different: 'different',
  Overlap: 'overlap',
  MapsTo: 'maps_to',
  MappedFrom: 'mapped_from',
  Superclass: 'superclass',
  Subclass: 'subclass',
  Other: 'other',
} as const;
/** @public Must stay in sync with `ReconRelation` in rust/server/src/api/structs.rs. */
export type ReconRelation = (typeof RECON_RELATION)[keyof typeof RECON_RELATION];

/** Must stay in sync with `ReconJudgmentEntry` in rust/server/src/api/structs.rs. */
export interface ReconJudgmentEntry {
  other: string;
  creator: string;
}

/** @public Must stay in sync with `ReconInfoResponse` in rust/server/src/api/structs.rs. */
export interface ReconInfoResponse {
  disjoint: ReconJudgmentEntry[];
  different: ReconJudgmentEntry[];
  overlap: ReconJudgmentEntry[];
  maps_to: ReconJudgmentEntry[];
  mapped_from: ReconJudgmentEntry[];
  superclass: ReconJudgmentEntry[];
  subclass: ReconJudgmentEntry[];
  other: ReconJudgmentEntry[];
}

/**
 * Structural relation wire identifiers accepted by the admin schema endpoint.
 * Must remain synchronized with rust/base/src/kr/consts.rs and the endpoint allowlist.
 */
export const STRUCTURAL_RELATION = {
  Same: '/core/same',
  SubclassOf: '/core/subclassof',
  SuperclassOf: '/core/superclassof',
  Overlap: '/core/overlap',
  Disjoint: '/core/disjoint',
  Different: '/core/different',
} as const;

/** Identifies one structural relation accepted by the admin schema endpoint. */
export type StructuralRelation = (typeof STRUCTURAL_RELATION)[keyof typeof STRUCTURAL_RELATION];

// IMPORTANT: Must be kept in sync with backend admin schema view structs
// in rust/server/src/api/structs.rs.
export interface TripleDto {
  sub: string;
  rel: string;
  obj: string;
}

/** Canonical relation used by concept-quality judgments. */
export const CONCEPT_QUALITY_RELATION = '/concept/quality';

/**
 * Canonical concept-quality judgment values.
 * Must remain synchronized with rust/base/src/kr/consts.rs.
 */
export const CONCEPT_QUALITY = {
  Good: '/concept/quality/good',
  Compositional: '/concept/quality/compositional',
  Broad: '/concept/quality/broad',
  Specific: '/concept/quality/specific',
  Ambiguous: '/concept/quality/ambiguous',
  Nonproduct: '/concept/quality/nonproduct',
  Redundant: '/concept/quality/redundant',
  Nonexistent: '/concept/quality/nonexistent',
  Incoherent: '/concept/quality/incoherent',
} as const;

/** Identifies one canonical concept-quality judgment value. */
export type ConceptQualityValue = (typeof CONCEPT_QUALITY)[keyof typeof CONCEPT_QUALITY];

/** Enumerates canonical concept-quality values in classification order. */
export const CONCEPT_QUALITY_VALUES = [
  CONCEPT_QUALITY.Good,
  CONCEPT_QUALITY.Compositional,
  CONCEPT_QUALITY.Broad,
  CONCEPT_QUALITY.Specific,
  CONCEPT_QUALITY.Ambiguous,
  CONCEPT_QUALITY.Nonproduct,
  CONCEPT_QUALITY.Redundant,
  CONCEPT_QUALITY.Nonexistent,
  CONCEPT_QUALITY.Incoherent,
] as const satisfies readonly ConceptQualityValue[];

// IMPORTANT: Must be kept in sync with backend admin schema view structs
// in rust/server/src/api/structs.rs.
export type SourceTableDto = 'recon' | 'schema';

// IMPORTANT: Must be kept in sync with backend admin schema view structs
// in rust/server/src/api/structs.rs.
export interface TripleProvenanceDto {
  creator: string;
  creator_is_llm: boolean;
  created_at?: string;
  justification?: string;
}

// IMPORTANT: Must be kept in sync with backend admin schema view structs
// in rust/server/src/api/structs.rs.
export type JudgmentKindDto = 'assert' | 'retract';

// IMPORTANT: Must be kept in sync with backend AdminSchemaJudgmentInputDto
// in rust/server/src/api/structs.rs.
export interface AdminSchemaJudgmentInputDto {
  triple: TripleDto;
  kind: JudgmentKindDto;
  justification?: string;
}

// IMPORTANT: Must be kept in sync with backend AdminSchemaCommitJudgmentsRequest
// in rust/server/src/api/structs.rs.
export interface AdminSchemaCommitJudgmentsRequest {
  judgments: AdminSchemaJudgmentInputDto[];
}

// IMPORTANT: Must be kept in sync with backend AdminSchemaCommitJudgmentsResponse
// in rust/server/src/api/structs.rs.
export interface AdminSchemaCommitJudgmentsResponse {
  committed_count: number;
  committed_at: string;
}

// IMPORTANT: Must be kept in sync with backend admin schema view structs
// in rust/server/src/api/structs.rs.
export interface EvidenceRowDto {
  source_table: SourceTableDto;
  kind: JudgmentKindDto;
  provenance?: TripleProvenanceDto;
}

// IMPORTANT: Must be kept in sync with backend admin schema view structs
// in rust/server/src/api/structs.rs.
export type ExclusionDto =
  | { type: 'retracted'; by_index: number }
  | {
      type: 'filtered_by_concept_quality';
      disqualifying_concept: string;
      quality: string;
    };

// IMPORTANT: Must be kept in sync with backend admin schema view structs
// in rust/server/src/api/structs.rs.
export type ResolutionDto =
  { status: 'active'; winner_index: number } | { status: 'inactive'; exclusions: ExclusionDto[] };

// IMPORTANT: Must be kept in sync with backend admin schema view structs
// in rust/server/src/api/structs.rs.
export interface ReducedTripleDto {
  triple: TripleDto;
  evidence: EvidenceRowDto[];
  resolution: ResolutionDto;
}

// IMPORTANT: Must be kept in sync with backend admin schema view structs
// in rust/server/src/api/structs.rs.
export interface ComposedSourceDto {
  member: string;
  reduced: ReducedTripleDto;
}

// IMPORTANT: Must be kept in sync with backend admin schema view structs
// in rust/server/src/api/structs.rs.
export interface ComposedReducedTripleDto {
  triple: TripleDto;
  sources: ComposedSourceDto[];
  resolution: ComposedResolutionDto;
}

// IMPORTANT: Must be kept in sync with backend admin schema view structs
// in rust/server/src/api/structs.rs.
export type ComposedResolutionDto = { status: 'active' } | { status: 'inactive' };

// IMPORTANT: Must be kept in sync with backend admin schema view structs
// in rust/server/src/api/structs.rs.
export interface LiteralSectionDto {
  anchor: string;
  active: ReducedTripleDto[];
  inactive: ReducedTripleDto[];
}

// IMPORTANT: Must be kept in sync with backend admin schema view structs
// in rust/server/src/api/structs.rs.
export interface ComposedSectionDto {
  anchor: string;
  active: ComposedReducedTripleDto[];
  inactive: ComposedReducedTripleDto[];
}

// IMPORTANT: Must be kept in sync with backend admin schema view structs
// in rust/server/src/api/structs.rs.
export interface AdminSchemaViewResponse {
  focus_cref: string;
  resolved_cref: string;
  focus: LiteralSectionDto;
  equivalence_class: LiteralSectionDto[];
  composed: ComposedSectionDto;
}

// Utility function to convert DisplayConceptDef to DisplayConceptRef
export function convertConceptDefToRef(cdef: DisplayConceptDef): DisplayConceptRef {
  return {
    cref: cdef.key,
    display: cdef.display_name,
    description: cdef.description,
    is_offer: cdef.key.startsWith(OFFER_NAMESPACE), // TODO(1): This should come from backend once is_offer field is available
  };
}

// Provenance information for documents and feed items.
// IMPORTANT: Must be kept in sync with backend DisplayProvenance in rust/server/src/api/structs.rs.
export interface Provenance {
  createdon: string;
  creator: string;
  avatar?: DisplayAvatar;
}

/** Post excerpt; keep in sync with DocSearchResult in rust/server/src/api/structs.rs. */
export interface DocSearchResult {
  /** Public identifier used to open the matching discussion. */
  doc_id: PublicDocId;
  /** Author and creation time of the matching post. */
  provenance: Provenance;
  /** Plain match or opening-document context, rendered without interpreting HTML or Markdown. */
  excerpt: string;
}

/** Search conversation; keep in sync with ThreadSearchResult in rust/server/src/api/structs.rs. */
export interface ThreadSearchResult {
  /** Highest-ranked visible match, used to order and open the conversation. */
  best_match: DocSearchResult;
  /** Additional visible matches in relevance order, bounded rather than exhaustive. */
  other_matches: DocSearchResult[];
  /** Readable opening-document context, absent when unavailable or already the best match. */
  opening_doc: DocSearchResult | null;
}

// Structured document types matching the Rust StructuredDocContent system
// IMPORTANT: Must be kept in sync with rust/base/src/kb/docs/structured_content.rs
export interface StructuredDoc {
  blocks: ContentBlock[];
}

/**
 * Field-restricted projection of the same topic represented by DisplayConceptRef,
 * not a distinct domain object. The separate type freezes the anonymous field
 * allowlist so broad display fields cannot enter this response implicitly.
 * IMPORTANT: Must be kept in sync with backend PublicReviewTopic in
 * rust/server/src/api/structs.rs.
 */
export interface PublicReviewTopic {
  cref: string;
  display: string;
  preview_image?: DisplayImage;
}

/**
 * Field-restricted projection of the same concept represented by DisplayConceptRef,
 * not a distinct domain object. The separate type freezes the anonymous field
 * allowlist and requires explicit review before exposing another display field.
 * IMPORTANT: Must be kept in sync with backend PublicReviewConcept in
 * rust/server/src/api/structs.rs.
 */
export interface PublicReviewConcept {
  cref: string;
  display: string;
  description: string;
  preview_image?: DisplayImage;
  is_offer: boolean;
}

/**
 * Field-restricted projection of the same document represented by DisplayDoc,
 * not a distinct domain object. The separate type constrains the anonymous
 * response so new DisplayDoc fields cannot enter it implicitly.
 * IMPORTANT: Must be kept in sync with backend PublicReviewProjection in
 * rust/server/src/api/structs.rs.
 */
export interface PublicReviewProjection {
  id: PublicDocId;
  structured_content: StructuredDoc;
  provenance: Provenance;
  topic?: PublicReviewTopic;
  mentioned_concepts: PublicReviewConcept[];
}

// Block-level elements in the document
export type ContentBlock =
  | { type: 'paragraph'; inline_elements: InlineElement[] }
  | { type: 'heading'; level: number; inline_elements: InlineElement[] }
  | { type: 'list'; ordered: boolean; items: StructuredListItem[] }
  | { type: 'table'; headers: TableRow; rows: TableRow[] }
  | { type: 'offer_comparison_table'; rows: OfferComparisonRow[] }
  | { type: 'block_quote'; blocks: ContentBlock[] }
  | { type: 'code_block'; language: string | null; code: string }
  | { type: 'thematic_break' };

// List item (can contain nested blocks)
export interface StructuredListItem {
  blocks: ContentBlock[];
}

// Table types
export interface TableRow {
  cells: TableCell[];
}

export interface TableCell {
  inline_elements: InlineElement[];
}

// Inline elements within a block
export type InlineElement =
  | { type: 'text'; content: string }
  | { type: 'concept_link'; text: InlineElement[]; cref: string }
  | { type: 'link'; text: InlineElement[]; url: string; title: string | null }
  | { type: 'image'; alt: string; url: string; title: string | null }
  | { type: 'emphasis'; content: InlineElement[] }
  | { type: 'strong'; content: InlineElement[] }
  | { type: 'code'; content: string }
  | { type: 'line_break' }
  | { type: 'soft_break' };

// IMPORTANT: Must be kept in sync with OfferComparisonTarget in
// rust/base/src/kb/docs/structured_content.rs.
export type OfferComparisonTarget =
  { kind: 'concept_ref'; cref: string } | { kind: 'external_url'; url: string };

// Offer comparison row
export interface OfferComparisonRow {
  target: OfferComparisonTarget;
  offer_name: string;
  comments: string;
}

// A document or post, as displayed to the user.
// All fields are actively used in the frontend based on audit performed
export interface DisplayDoc {
  id: PublicDocId;
  text: string;
  // Corresponds to the backend DisplayDoc.topic field.
  // The backend may omit this field when no topic is available.
  topic?: DisplayConceptRef;
  // Corresponds to the backend DisplayDoc.aspect field.
  // The backend may omit this field when no aspect is available.
  aspect?: DisplayConceptRef;
  citations: DisplayCitation[];
  provenance: Provenance;
  // Explicit visibility specification for this document, when present.
  // Docs arriving here have already passed server-side access checks; this
  // field records the explicitly chosen spec for rendering indicators.
  // Effective visibility is enforced server-side and depends on rules beyond
  // this field.  IMPORTANT: Must be kept in sync with backend
  // DisplayDoc.visibility.
  visibility: DocVisibility | null;
  mentioned_concepts: DisplayConceptRef[];
  // TODO(1): reconsider if this is the best way to attach to a document
  suggested_questions: string[];
  // Summary text for guide documents (optional)
  summary?: string;
  // Structured document content (new system)
  structured_content?: StructuredDoc;
  // Vote info for authenticated users (absent for unauthenticated)
  // IMPORTANT: Must be kept in sync with backend DisplayDoc.vote_info
  // in rust/server/src/api/structs.rs
  vote_info?: DisclosedVoteInfo;
}

export interface DisplayCitation {
  doc: PublicDocId;
  rel: string;
}

// IMPORTANT: Keep this tagged thread contract synchronized with ThreadResponse and
// ThreadNode in rust/server/src/api/structs.rs. Wire tags and fields use snake_case.
export const THREAD_NODE_KIND = {
  Document: 'document',
  Inaccessible: 'inaccessible',
} as const;

// Keep in sync with ThreadNode::Document in rust/server/src/api/structs.rs.
export interface ThreadDocumentNode {
  kind: typeof THREAD_NODE_KIND.Document;
  public_doc_id: PublicDocId;
  parent_public_doc_id: PublicDocId | null;
  doc: DisplayDoc;
}

// Keep in sync with ThreadNode::Inaccessible in rust/server/src/api/structs.rs.
export interface InaccessibleThreadNode {
  kind: typeof THREAD_NODE_KIND.Inaccessible;
  public_doc_id: PublicDocId;
  parent_public_doc_id: PublicDocId | null;
}

// Keep in sync with ThreadNode in rust/server/src/api/structs.rs.
export type ThreadNode = ThreadDocumentNode | InaccessibleThreadNode;

/** Checks the required top-level shape consumed by feed document rendering. */
function isRuntimeDisplayDoc(value: unknown): value is DisplayDoc {
  if (typeof value !== 'object' || value === null) return false;

  /** Provides untyped access to the candidate document payload. */
  const candidate = value as Record<string, unknown>;
  /** Provides the nested provenance payload checked below. */
  const provenance = candidate.provenance;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.text === 'string' &&
    (candidate.topic === undefined ||
      (typeof candidate.topic === 'object' && candidate.topic !== null)) &&
    (candidate.aspect === undefined ||
      (typeof candidate.aspect === 'object' && candidate.aspect !== null)) &&
    Array.isArray(candidate.citations) &&
    typeof provenance === 'object' &&
    provenance !== null &&
    typeof (provenance as Record<string, unknown>).creator === 'string' &&
    typeof (provenance as Record<string, unknown>).createdon === 'string' &&
    (candidate.visibility === null ||
      candidate.visibility === DOC_VISIBILITY.Public ||
      candidate.visibility === DOC_VISIBILITY.Friends ||
      candidate.visibility === DOC_VISIBILITY.Private) &&
    Array.isArray(candidate.mentioned_concepts) &&
    Array.isArray(candidate.suggested_questions)
  );
}

/** Keeps only recognized runtime thread nodes from an unvalidated API payload. */
export function normalizeThreadNodes(nodes: unknown): ThreadNode[] {
  if (!Array.isArray(nodes)) return [];

  return nodes.filter((node): node is ThreadNode => {
    if (typeof node !== 'object' || node === null) return false;

    const candidate = node as Record<string, unknown>;
    if (typeof candidate.public_doc_id !== 'string') return false;
    if (
      candidate.parent_public_doc_id !== null &&
      typeof candidate.parent_public_doc_id !== 'string'
    ) {
      return false;
    }

    if (candidate.kind === THREAD_NODE_KIND.Inaccessible) return true;
    return (
      candidate.kind === THREAD_NODE_KIND.Document &&
      isRuntimeDisplayDoc(candidate.doc) &&
      candidate.doc.id === candidate.public_doc_id
    );
  });
}

// Keep in sync with ThreadResponse in rust/server/src/api/structs.rs.
export interface ThreadResponse {
  target_public_doc_id: PublicDocId;
  nodes: ThreadNode[];
}

// Keep in sync with FeedItemSuggestedQuestion in rust/server/src/api/structs.rs.
export interface FeedItemSuggestedQuestion {
  /** Text posted when the viewer selects the question. */
  text: string;
  /** Public ID of the document that supplied the question. */
  source_doc_id: PublicDocId;
  /** Source-document topic when available. */
  topic_cref?: string;
}

// A single topic entry in a thread's table of contents
export interface ThreadTopicEntry {
  doc_id: PublicDocId;
  summary: string;
}

// Table of contents for a thread
export interface ThreadTableOfContents {
  topics: ThreadTopicEntry[];
}

// A single observed price for an offer at a point in time.
// IMPORTANT: Keep in sync with backend PricePoint in rust/skuindex/src/kb/sku/provider_data.rs.
export interface PricePoint {
  date: string; // ISO 8601 timestamp (serde DateTime<Utc>)
  price: number;
  currency: string | null;
}

// Define type of the json structure we get back.
// IMPORTANT: Keep in sync with backend DisplayOffer in rust/server/src/api/structs.rs.
export interface DisplayOffer {
  id: string;
  marketplace: string;
  title: string;
  orig_title: string | null;
  description: string;
  vendor: string;
  images: DisplayImage[];
  price: number | null;
  currency: string | null;
  category_path: string | null;
  offer_url: string | null;
  customer_rating: number | null;
  num_reviews: number | null;
  stock_status: string | null;
  tags: string[];
  // Chronological price observations (oldest first); empty when unavailable.
  price_history: PricePoint[];
  buy_at_label: string;
}

// A feed item
export interface FeedItem {
  datetime: string | null; // timestamp
  // Keep in sync with `FeedItem.thread` and `ThreadResponse` in rust/server/src/api/structs.rs.
  thread: ThreadResponse; // Nodes in chronological order
  creators: string[];
  topics: DisplayConceptRef[];
  offers: DisplayOffer[];
  suggested_questions: FeedItemSuggestedQuestion[];
  justification: FeedItemJustification;
  summary?: DisplayDoc; // Thread summary for card display
  public_review_id?: PublicDocId;
}

// Justification for a feed item
interface FeedItemJustification {
  // In case of the user being mentioned
  mentioner: string | null;
  // Exact paragraph containing the viewer's explicit mention. Keep in sync with the backend.
  mention_paragraph?: string;

  // In case of this being content the user follows
  friend: string | null; // Follower or followee
  topic: DisplayConceptRef | null;
  aspect: DisplayConceptRef | null;

  // In case of this being a thread the user started or
  // added questions to
  is_owned: boolean;
}

// Info about a follow for a user (typically the current user).  Contains
// the username of the followed user, and the topics for the follow.
export interface DisplayUserFollow {
  username: string;
  avatar: DisplayAvatar;
  topics: DisplayConceptRef[];
}

// Some additional info for the user that's generally useful in the UI.
interface DisplayCollection {
  name: string;
  docs: PublicDocId[];
  offers: DisplayConceptRef[];
}

// A consolidated grouping of the thick data for all the items in the
// user's collections.
export interface CollectionsPayload {
  docs: DisplayDoc[];
  offers: DisplayOffer[];
}

/** Must stay in sync with `AddOfferByUrlRequest` in rust/server/src/api/structs.rs. */
export interface AddOfferByUrlRequest {
  url: string;
}

export interface DisplayAvatar {
  url: string;
}

/**
 * Runtime avatar source identifiers shared with the backend contract.
 * Must be kept in sync with rust/base/src/kb/user/mod.rs.
 */
export const AVATAR_SOURCE = {
  DefaultPlaceholder: 'default_placeholder',
  GeneratedInitials: 'generated_initials',
} as const;
export type AvatarSource = (typeof AVATAR_SOURCE)[keyof typeof AVATAR_SOURCE];

export const AVATAR_SOURCE_DISPLAY_ORDER: readonly AvatarSource[] = [
  AVATAR_SOURCE.DefaultPlaceholder,
  AVATAR_SOURCE.GeneratedInitials,
] as const;

export function assertAvatarSourceDisplayOrderIsExhaustive(): void {
  const all = Object.values(AVATAR_SOURCE);
  const order = [...AVATAR_SOURCE_DISPLAY_ORDER];

  const unique = new Set(order);
  if (unique.size !== order.length) {
    throw new Error('Duplicate avatar source in display order');
  }

  const missing = all.filter((source) => !unique.has(source));
  const extra = order.filter((source) => !all.includes(source));

  if (missing.length > 0 || extra.length > 0) {
    throw new Error(
      `Avatar source display order mismatch: missing=${missing.join(',')} extra=${extra.join(',')}`
    );
  }
}

assertAvatarSourceDisplayOrderIsExhaustive();

export interface DefaultPlaceholderAvatarConfig {}

export interface GeneratedInitialsAvatarConfig {
  initials: string;
  background_color: string;
}

export interface AvatarSettingsSource<TConfig> {
  available: boolean;
  config: TConfig;
}

export interface AvatarSettingsOptions {
  default_placeholder: AvatarSettingsSource<DefaultPlaceholderAvatarConfig>;
  generated_initials: AvatarSettingsSource<GeneratedInitialsAvatarConfig>;
}

export interface AvatarSettingsSuggestions {
  generated_initials?: string;
}

export interface AvatarSettingsResponse {
  selected: AvatarSource;
  options: AvatarSettingsOptions;
  generated_initials_preset_colors: string[];
  suggestions: AvatarSettingsSuggestions;
}

export interface AvatarSettingsPatchConfigs {
  default_placeholder?: DefaultPlaceholderAvatarConfig;
  generated_initials?: GeneratedInitialsAvatarConfig;
}

export interface PatchAvatarSettingsRequest {
  selected?: AvatarSource;
  configs?: AvatarSettingsPatchConfigs;
}

export const AVATAR_VALIDATION_ERROR_CODE = {
  Required: 'required',
  TooLong: 'too_long',
  InvalidCharacters: 'invalid_characters',
  InvalidHexColor: 'invalid_hex_color',
} as const;
export type AvatarValidationErrorCode =
  (typeof AVATAR_VALIDATION_ERROR_CODE)[keyof typeof AVATAR_VALIDATION_ERROR_CODE];

export interface AvatarValidationErrors {
  initials?: AvatarValidationErrorCode;
  background_color?: AvatarValidationErrorCode;
}

// Avatar validation error payload returned by avatar settings and preview endpoints.
// IMPORTANT: Must be kept in sync with backend AvatarValidationErrorResponse struct
// in rust/server/src/api/structs.rs
export interface AvatarValidationErrorResponse {
  success: false;
  status: 'invalid_config';
  errors: AvatarValidationErrors;
}

// Avatar preview request payload shared with the backend avatar preview API.
// IMPORTANT: Must be kept in sync with backend AvatarPreviewRequest enum
// in rust/server/src/api/structs.rs
export type AvatarPreviewRequest =
  | {
      source: typeof AVATAR_SOURCE.DefaultPlaceholder;
      config: DefaultPlaceholderAvatarConfig;
    }
  | {
      source: typeof AVATAR_SOURCE.GeneratedInitials;
      config: GeneratedInitialsAvatarConfig;
    };

type AvatarPreviewResult =
  | {
      success: true;
      svg: Blob;
    }
  | AvatarValidationErrorResponse;

export function resolveEffectiveAvatarUrl(url: string): string {
  if (url.startsWith('/user/avatar/')) {
    return `${API_URL}${url}`;
  }

  return url;
}

/**
 * Runtime password-state identifiers shared with the backend contract.
 * Must be kept in sync with rust/base/src/kb/user/mod.rs and rust/server/src/api/structs.rs.
 */
export const PASSWORD_STATE = {
  UserSet: 'user_set',
  SystemGenerated: 'system_generated',
  Temporary: 'temporary',
  ResetRequired: 'reset_required',
  ResetInitiated: 'reset_initiated',
} as const;
/** Union of password-state identifiers returned by the backend. */
export type PasswordState = (typeof PASSWORD_STATE)[keyof typeof PASSWORD_STATE];

export interface DisplayUser {
  /**
   * Stable account identifier; unlike `username` and `email`, it never changes.
   */
  public_id: PublicUserId;
  username: string;
  fullname: string;
  email: string;
  pending_email?: string;
  link: string;
  avatar: DisplayAvatar;
  has_been_welcomed: boolean;
  may_invite: boolean;
  followed_topics: DisplayConceptRef[];
  followed_users: DisplayUserFollow[];
  collections: DisplayCollection[];
  oauth_identities?: Record<string, OAuthIdentityInfo>;
  has_password_auth: boolean;
  /** Password-authentication state returned by the backend. */
  password_state: PasswordState;
  has_set_password: boolean;
  profile_discoverability: ProfileDiscoverability;
  marketplace_preference: MarketplacePreference;
  // Admin flag computed by backend (backend is security boundary)
  // IMPORTANT: Keep in sync with backend DisplayUser struct in rust/server/src/api/structs.rs
  is_admin: boolean;
}

/**
 * Runtime profile discoverability identifiers shared with the backend contract.
 * Must be kept in sync with rust/server/src/api/structs.rs.
 */
export const PROFILE_DISCOVERABILITY = {
  Public: 'public',
  Connections: 'connections',
  Private: 'private',
} as const;
export type ProfileDiscoverability =
  (typeof PROFILE_DISCOVERABILITY)[keyof typeof PROFILE_DISCOVERABILITY];

/** Enumerates profile discoverability values in UI display order. */
export const PROFILE_DISCOVERABILITY_VALUES = [
  PROFILE_DISCOVERABILITY.Public,
  PROFILE_DISCOVERABILITY.Connections,
  PROFILE_DISCOVERABILITY.Private,
] as const satisfies readonly ProfileDiscoverability[];

/**
 * Runtime marketplace identifiers shared with the backend contract.
 * Must be kept in sync with rust/base/src/kb/sku/marketplace.rs.
 */
export const MARKETPLACE = {
  Amazon: 'amazon',
  Walmart: 'walmart',
  Shopify: 'shopify',
} as const;
export type Marketplace = (typeof MARKETPLACE)[keyof typeof MARKETPLACE];

/** Maps marketplace identifiers to the labels shown in the frontend. */
export const MARKETPLACE_DISPLAY_NAMES: Record<Marketplace, string> = {
  [MARKETPLACE.Amazon]: 'Amazon',
  [MARKETPLACE.Walmart]: 'Walmart',
  [MARKETPLACE.Shopify]: 'Shopify',
};

/** Lists all supported marketplaces in the order expected by marketplace controls. */
export const ALL_MARKETPLACES = [
  MARKETPLACE.Amazon,
  MARKETPLACE.Walmart,
  MARKETPLACE.Shopify,
] as const satisfies readonly Marketplace[];

// Shopping marketplace preference
// IMPORTANT: Must be kept in sync with backend MarketplaceAffinity struct
// in rust/base/src/integrations/orchestrator/types.rs
export interface MarketplacePreference {
  preferred: Marketplace[];
  avoided: Marketplace[];
}

// Minimal user search result
// IMPORTANT: Must be kept in sync with backend UserSearchResult struct
// in rust/server/src/api/structs.rs
export interface UserSearchResult {
  username: string;
  fullname: string;
  avatar: DisplayAvatar;
  i_follow: boolean;
  follows_me: boolean;
  mutual: boolean;
  shared_topics: DisplayConceptRef[];
  shared_topics_count: number;
}

// Public profile view
// IMPORTANT: Must be kept in sync with backend PublicUserProfile struct
// in rust/server/src/api/structs.rs
export interface PublicUserProfile {
  username: string;
  fullname: string;
  avatar: DisplayAvatar;
  followed_topics: DisplayConceptRef[];
  followed_users: DisplayUserFollow[];
  i_follow: boolean;
  follows_me: boolean;
  mutual: boolean;
}

// A unified request to update follows (both topics and users)
interface UpdateFollowsRequest {
  action: UpdateFollowAction;
  username_to_follow?: string;
  topics?: string[];
}

//////////////////////////////////////////////////////////////////////
//
// Shared components that call the backend
//

// Shared fetcher with error handling
export const urlFetcher = (url: string) =>
  fetch(url, {
    credentials: 'include', // Add this line
  }).then((res) => {
    if (!res.ok) throw Error(res.statusText);
    return res.json();
  });

// Not sure how widely these are used.  Also not sure if they should be
// used vs. the useSWR hook.
export enum LoadStatus {
  NoArg,
  Error,
  IsLoading,
  EmptyResponse,
  Success,
}

/**
 * Generic hook for fetching data with LoadStatus handling.
 *
 * @param url - The URL to fetch data from
 * @param argCheck - Whether the arguments are valid for fetching
 * @param dataTransform - Optional transform function applied to fetched data.
 *   IMPORTANT: This function should be pure and not capture changing values.
 *   If your transform depends on reactive state, wrap it with useCallback:
 *   ```
 *   const transform = useCallback((data) =>
 *     data.filter(x => x.id === selectedId), [selectedId]
 *   );
 *   ```
 * @returns A tuple of [LoadStatus, transformed data or null]
 */
export function useGetData<T>(
  url: string,
  argCheck: boolean,
  dataTransform?: (data: any) => T
): [LoadStatus, T | null] {
  const { data, error, isLoading } = useSWR(url, urlFetcher);

  const [status, setStatus] = useState<LoadStatus>(LoadStatus.IsLoading);
  const [result, setResult] = useState<T | null>(null);

  useEffect(() => {
    if (!argCheck) {
      setStatus(LoadStatus.NoArg);
      setResult(null);
    } else if (error) {
      setStatus(LoadStatus.Error);
      setResult(null);
    } else if (isLoading) {
      setStatus(LoadStatus.IsLoading);
      setResult(null);
    } else if (!data || (typeof data === 'string' && data.length === 0)) {
      setStatus(LoadStatus.EmptyResponse);
      setResult(null);
    } else {
      setStatus(LoadStatus.Success);
      // Apply transformation if provided, otherwise use data as-is
      const transformedData = dataTransform ? dataTransform(data) : (data as T);
      setResult(transformedData);
    }
    // dataTransform intentionally excluded from deps - it's logically stable and callers
    // pass simple transform functions that don't capture changing values. Including it
    // causes infinite re-renders when callers use inline arrow functions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [argCheck, data, error, isLoading]);

  return [status, result];
}

// Load a concept definition from the backend.
export function useGetCDef(topic: string): [LoadStatus, DisplayConceptDef | null] {
  // Encode cref keys as a single path segment so slash-containing RefH values
  // still match the backend `/schema/{refs}` route. Callers must pass decoded
  // cref strings into this hook.
  const url = topic ? `${API_URL}/schema/${encodeURIComponent(topic)}` : '';
  return useGetData<DisplayConceptDef>(url, topic !== '');
}

/** Builds the SWR key for one unified admin schema view. */
export function adminSchemaViewUrl(cref: string): string {
  return cref ? `${API_URL}/admin/schema/view/${encodeURIComponent(cref)}` : '';
}

/** Public concept-search endpoint consumed by navigation-oriented selectors. */
export const SCHEMA_PREFIX_SEARCH_PATH = '/schema/prefix_search';

/** Admin concept-search endpoint that includes schema-editor concepts. */
export const ADMIN_SCHEMA_PREFIX_SEARCH_PATH = '/admin/schema/prefix_search';

/** Refetches the cached unified admin schema view for one concept. */
export async function revalidateAdminSchemaView(cref: string): Promise<void> {
  const key = adminSchemaViewUrl(cref);
  if (key) {
    await mutate(key);
  }
}

// Load the unified admin schema view from the backend.
export function useGetAdminSchemaView(cref: string): [LoadStatus, AdminSchemaViewResponse | null] {
  const url = adminSchemaViewUrl(cref);
  return useGetData<AdminSchemaViewResponse>(url, cref !== '');
}
//////////////////////////////////////////////////////////////////////
//
// Shared functions for calling the backend.
//

export async function updateFollows(
  req_fields: Partial<UpdateFollowsRequest>
): Promise<DisplayUser> {
  const request: UpdateFollowsRequest = {
    action: UPDATE_FOLLOW_ACTION.Follow,
    ...req_fields,
  };

  const resp = await fetch(`${API_URL}/user/update_follows`, {
    method: 'POST',
    credentials: 'include', // Add this
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!resp.ok) {
    throw new Error(`Error updating follows: ${resp.statusText}`);
  }

  return resp.json();
}

/** Fetches a marketplace offer by URL and adds it to the default collection. */
export async function addOfferByUrl(url: string): Promise<SuccessResponse> {
  const request: AddOfferByUrlRequest = { url };
  const response = await fetch(`${API_URL}/user/collections/add_offer_by_url`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error('Failed to add product');
  }

  return response.json();
}

/** Atomically commits the admin schema editor's queued judgments. */
export async function commitAdminSchemaJudgments(
  request: AdminSchemaCommitJudgmentsRequest
): Promise<AdminSchemaCommitJudgmentsResponse> {
  // TODO(1): This non-idempotent POST has no timeout or abort. A bare
  // AbortSignal.timeout() is unsafe because the server may commit after the client loses
  // the response, and retrying the preserved draft would double-submit. Add an idempotency
  // key or a reconciliation protocol before making an ambiguous result retryable.
  const resp = await fetch(`${API_URL}/admin/schema/judgments`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!resp.ok) {
    throw new Error(`Failed to commit schema judgments: HTTP ${resp.status}`);
  }

  return resp.json();
}

// Cast or remove a vote on a document
// IMPORTANT: Must be kept in sync with backend CastVoteRequest/CastVoteResponse/CastVoteStatus
// in rust/server/src/api/vote.rs

// Status codes for cast vote operations
// IMPORTANT: Must be kept in sync with backend CastVoteStatus enum
// in rust/server/src/api/vote.rs
export type CastVoteStatus = 'success' | 'rate_limited' | 'self_vote_not_allowed' | 'doc_not_found';

// Response for cast vote operation
// IMPORTANT: Must be kept in sync with backend CastVoteResponse struct
// in rust/server/src/api/vote.rs
export interface CastVoteResponse {
  status: CastVoteStatus;
  vote_info?: DisclosedVoteInfo;
}

export async function castVote(
  docId: PublicDocId,
  voteAction: VoteAction
): Promise<CastVoteResponse> {
  const resp = await fetch(`${API_URL}/doc/vote`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ doc_id: docId, vote_action: voteAction }),
  });

  if (!resp.ok) {
    throw new Error(`Error casting vote: ${resp.statusText}`);
  }

  return resp.json();
}

/** Sanitized non-success response from the irreversible document-hide endpoint. */
export class HideDocumentError extends Error {
  constructor(public readonly status: number) {
    super(`Unable to hide document (HTTP ${status}).`);
    this.name = 'HideDocumentError';
  }
}

/** Irreversibly hides an authored document and accepts only the empty 204 contract. */
export async function hideDocument(docId: PublicDocId): Promise<void> {
  const resp = await fetch(`${API_URL}/doc/hide/${encodeURIComponent(docId)}`, {
    method: 'POST',
    credentials: 'include',
  });

  if (resp.status !== 204) {
    throw new HideDocumentError(resp.status);
  }
}

// Update the user's profile discoverability setting
export async function setProfileDiscoverability(
  discoverability: ProfileDiscoverability
): Promise<DisplayUser> {
  const resp = await fetch(`${API_URL}/user/set_discoverability`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ discoverability }),
  });

  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
  }

  return resp.json();
}

// Update the user's marketplace preference setting
export async function setMarketplacePreference(
  marketplace_preference: MarketplacePreference
): Promise<DisplayUser> {
  const resp = await fetch(`${API_URL}/user/set_marketplace_preference`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ marketplace_preference }),
  });

  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
  }

  return resp.json();
}

// Load the current avatar settings and available avatar source options.
export async function getAvatarSettings(): Promise<AvatarSettingsResponse> {
  const resp = await fetch(`${API_URL}/user/avatar-settings`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
  }

  return resp.json();
}

// Update the selected avatar source and its editable configuration.
export async function patchAvatarSettings(
  request: PatchAvatarSettingsRequest
): Promise<AvatarSettingsResponse | AvatarValidationErrorResponse> {
  const resp = await fetch(`${API_URL}/user/avatar-settings`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
  }

  return resp.json();
}

// Request an avatar preview, returning either rendered SVG or validation errors.
export async function postAvatarPreview(
  request: AvatarPreviewRequest
): Promise<AvatarPreviewResult> {
  const resp = await fetch(`${API_URL}/user/avatar-preview`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
  }

  const contentType = resp.headers.get('content-type') ?? '';
  if (contentType.startsWith('image/svg+xml')) {
    return {
      success: true,
      svg: await resp.blob(),
    };
  }

  return resp.json();
}

// Response from password strength check
interface PasswordStrengthResponse {
  strength: number;
  is_acceptable: boolean;
}

/** @public Unsupported email format reasons - keep in sync with rust/server/src/api/structs.rs */
export const EmailUnsupportedReason = {
  Empty: 'empty',
  MultipleAddresses: 'multiple_addresses',
  DisplayNameOrAngleAddress: 'display_name_or_angle_address',
  CommentsOrCfws: 'comments_or_cfws',
  QuotedLocalPart: 'quoted_local_part',
  NonAscii: 'non_ascii',
  InternalWhitespace: 'internal_whitespace',
  MissingAtSign: 'missing_at_sign',
  MultipleAtSigns: 'multiple_at_signs',
  InvalidLocalPart: 'invalid_local_part',
  InvalidDomain: 'invalid_domain',
  DomainLiteral: 'domain_literal',
} as const;
export type EmailUnsupportedReason =
  (typeof EmailUnsupportedReason)[keyof typeof EmailUnsupportedReason];

/**
 * @public
 * Internally status-tagged registration response returned by the backend.
 * Must stay in sync with `RegistrationResponse` in rust/server/src/auth.rs.
 */
export type RegistrationResponse =
  | { status: typeof RegistrationStatus.Success }
  | { status: typeof RegistrationStatus.VerificationEmailSendFailed }
  | { status: typeof RegistrationStatus.UnsupportedFormat; reason: EmailUnsupportedReason }
  | { status: typeof RegistrationStatus.UsernameInvalid; username: string }
  | { status: typeof RegistrationStatus.UsernameTaken; username: string }
  | { status: typeof RegistrationStatus.InvalidInviteCode }
  | { status: typeof RegistrationStatus.PasswordInsecure }
  | { status: typeof RegistrationStatus.PasswordMismatch }
  | { status: typeof RegistrationStatus.EmailMismatch }
  | { status: typeof RegistrationStatus.PasswordRequired }
  | { status: typeof RegistrationStatus.EmailRequired }
  | { status: typeof RegistrationStatus.EmailAlreadyInUse; email?: string };

/** @public API status values returned when a verification token is consumed - keep in sync with backend */
export const VerifyEmailStatus = {
  /** @public */ Success: 'success',
  /** @public */ InvalidToken: 'invalid_token',
  /** @public */ ExpiredToken: 'expired_token',
  /** @public */ EmailAlreadyInUse: 'email_already_in_use',
} as const;
export type VerifyEmailStatus = (typeof VerifyEmailStatus)[keyof typeof VerifyEmailStatus];

/** @public Request body for completing a pending email verification by token - keep in sync with backend */
export interface VerifyEmailRequest {
  token: string;
}

/** @public Response body for email verification token consumption - keep in sync with backend */
export interface VerifyEmailResponse {
  success: boolean;
  status: VerifyEmailStatus;
}

/** @public API status values for starting a pending email change - keep in sync with backend */
export const UpdateEmailStatus = {
  /** @public */ PendingVerificationStarted: 'pending_verification_started',
  /** @public */ UnsupportedFormat: 'unsupported_format',
  /** @public */ AlreadyVerifiedByYou: 'already_verified_by_you',
  /** @public */ AlreadyPendingForYou: 'already_pending_for_you',
  /** @public */ ClaimedByAnotherUser: 'claimed_by_another_user',
  /** @public */ EmailRequired: 'email_required',
} as const;
export type UpdateEmailStatus = (typeof UpdateEmailStatus)[keyof typeof UpdateEmailStatus];

/** @public Response body for starting a pending email verification - keep in sync with backend */
export interface UpdateEmailResponse {
  success: boolean;
  status: UpdateEmailStatus;
  reason?: EmailUnsupportedReason;
}

/** @public API status values for resending the latest pending email verification - keep in sync with backend */
export const ResendEmailVerificationStatus = {
  /** @public */ VerificationEmailSent: 'verification_email_sent',
  /** @public */ NoPendingVerification: 'no_pending_verification',
} as const;
export type ResendEmailVerificationStatus =
  (typeof ResendEmailVerificationStatus)[keyof typeof ResendEmailVerificationStatus];

/** @public Response body for resending a pending email verification - keep in sync with backend */
export interface ResendEmailVerificationResponse {
  success: boolean;
  status: ResendEmailVerificationStatus;
}

/** @public API status values for abandoning a pending email verification - keep in sync with backend */
export const StopEmailVerificationStatus = {
  /** @public */ PendingVerificationStopped: 'pending_verification_stopped',
  /** @public */ NoPendingVerification: 'no_pending_verification',
} as const;
export type StopEmailVerificationStatus =
  (typeof StopEmailVerificationStatus)[keyof typeof StopEmailVerificationStatus];

/** @public Response body for stopping a pending email verification - keep in sync with backend */
export interface StopEmailVerificationResponse {
  success: boolean;
  status: StopEmailVerificationStatus;
}

/**
 * @public Request body for setting or changing the current user's password.
 * Keep in sync with `UpdatePasswordRequest` in rust/server/src/api/structs.rs.
 * `old_password` is omitted for first-time password setup (the backend defaults it).
 */
export interface UpdatePasswordRequest {
  old_password?: string;
  new_password: string;
}

/**
 * @public Request body for removing a linked OAuth identity from the account.
 * Keep in sync with `RemoveOAuthIdentityRequest` in rust/server/src/api/user.rs.
 */
export interface RemoveOAuthIdentityRequest {
  provider: OAuthProvider;
  provider_user_id: string;
}

// Password reset types - Must be kept in sync with backend (server/src/api/structs.rs)

/** @public API type for password reset - keep in sync with backend */
export interface RequestPasswordResetRequest {
  email: string;
}

/** @public API status values for password reset requests - keep in sync with backend */
export const RequestPasswordResetStatus = {
  /** @public */ Accepted: 'accepted',
  /** @public */ UnsupportedFormat: 'unsupported_format',
} as const;
export type RequestPasswordResetStatusValue =
  (typeof RequestPasswordResetStatus)[keyof typeof RequestPasswordResetStatus];

/** @public API type for password reset - keep in sync with backend */
export interface RequestPasswordResetResponse {
  accepted: boolean;
  status: RequestPasswordResetStatusValue;
  reason?: EmailUnsupportedReason;
}

/** @public API enum for password reset status - keep in sync with backend */
export const ResetPasswordStatus = {
  /** @public */ Success: 'success',
  /** @public */ InvalidToken: 'invalid_token',
  /** @public */ ExpiredToken: 'expired_token',
  /** @public */ PasswordInsecure: 'password_insecure',
} as const;
export type ResetPasswordStatus = (typeof ResetPasswordStatus)[keyof typeof ResetPasswordStatus];

/** @public API type for password reset - keep in sync with backend */
export interface ResetPasswordRequest {
  token: string;
  new_password: string;
}

/** @public API type for password reset - keep in sync with backend */
export interface ResetPasswordResponse {
  success: boolean;
  status: ResetPasswordStatus;
  score?: number;
}

/** @public API enum for reset token validation - keep in sync with backend */
export const ValidateResetTokenStatus = {
  /** @public */ Valid: 'valid',
  /** @public */ InvalidToken: 'invalid_token',
  /** @public */ ExpiredToken: 'expired_token',
} as const;
export type ValidateResetTokenStatus =
  (typeof ValidateResetTokenStatus)[keyof typeof ValidateResetTokenStatus];

/** @public request type for reset token validation - keep in sync with backend */
export interface ValidateResetTokenRequest {
  token: string;
}

/** @public response type for reset token validation - keep in sync with backend */
export interface ValidateResetTokenResponse {
  success: boolean;
  status: ValidateResetTokenStatus;
}

/**
 * Type of offer post to create (review or question)
 * Must be kept in sync with the backend OfferPostType enum
 */
export enum OfferPostType {
  Review = 'review',
  Question = 'question',
}

/**
 * Request to create a new document
 * Must be kept in sync with the backend NewDocRequest struct
 */
export interface NewDocRequest {
  /** Content of the document */
  content: string;

  /** Topic of the document (optional) */
  topic?: string;

  /** Visibility of the document */
  visibility?: DocVisibility;

  /** Citation document id to reference (optional) */
  citation?: PublicDocId;

  /** Type of offer post (if applicable) */
  post_type?: OfferPostType;
}

/**
 * Successful response from the new-document endpoint.
 * Must be kept in sync with the backend response from POST /doc/new.
 */
export interface NewDocResponse {
  /** Public identifier of the newly created document. */
  doc_id: PublicDocId;
}

/** @public Must be kept in sync with `TriggerReplyStatus` in rust/server/src/api/structs.rs. */
export type TriggerReplyStatus = 'triggered';

/** @public Must be kept in sync with `TriggerReplyResponse` in rust/server/src/api/structs.rs. */
export interface TriggerReplyResponse {
  status: TriggerReplyStatus;
  doc_id: PublicDocId;
}

/** Reports an unsuccessful HTTP response from POST /doc/new. */
export class NewDocRequestError extends Error {
  constructor(public readonly status: number) {
    super(`Failed to post content (${status})`);
    this.name = 'NewDocRequestError';
  }
}

/** Validates and narrows an untrusted successful POST /doc/new response. */
function parseNewDocResponse(value: unknown): NewDocResponse {
  if (
    typeof value !== 'object' ||
    value === null ||
    !('doc_id' in value) ||
    typeof value.doc_id !== 'string' ||
    value.doc_id.trim().length === 0
  ) {
    throw new Error('Invalid new-document response');
  }

  return { doc_id: asPublicDocId(value.doc_id) };
}

/** Creates a document through the typed endpoint contract and validates its response. */
export async function createNewDoc(request: NewDocRequest): Promise<NewDocResponse> {
  const response = await fetch(`${API_URL}/doc/new`, {
    credentials: 'include',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new NewDocRequestError(response.status);
  }

  return parseNewDocResponse(await response.json());
}

// Dynamically check password strength.
export async function checkPasswordStrength(password: string): Promise<PasswordStrengthResponse> {
  if (!password) {
    return { strength: 0, is_acceptable: false };
  }

  const resp = await fetch(`${API_URL}/auth/password_strength_check`, {
    credentials: 'include',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });

  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}`);
  }

  const data = await resp.json();
  return data;
}

// Validate a password reset token (read-only operation)
export async function validateResetToken(token: string): Promise<ValidateResetTokenResponse> {
  const resp = await fetch(`${API_URL}/auth/validate_reset_token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token } satisfies ValidateResetTokenRequest),
  });

  // Application-level errors are HTTP 200; only network/HTTP errors throw
  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}`);
  }
  return resp.json();
}

/** Posts a verification token and returns the backend's terminal verification result. */
export async function verifyEmail(token: string): Promise<VerifyEmailResponse> {
  const resp = await fetch(`${API_URL}/auth/verify_email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token } satisfies VerifyEmailRequest),
  });

  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}`);
  }

  return resp.json();
}

// Check if a username is available
export async function checkUsernameAvailability(
  username: string
): Promise<{ available: boolean; error?: string }> {
  try {
    const resp = await fetch(`${API_URL}/auth/check_username`, {
      credentials: 'include',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username }),
    });

    if (resp.ok) {
      const data = await resp.json();
      return { available: data.available };
    } else {
      const errorEnvelope = await parseApiErrorResponse(resp);
      return {
        available: false,
        error: apiErrorMessage({ operation: 'username_check' }, resp.status, errorEnvelope),
      };
    }
  } catch (error: unknown) {
    console.error('Error checking username availability:', error);
    return {
      available: false,
      error: API_ERROR_MESSAGES.username_check,
    };
  }
}

/** Starts verification for a new email while leaving the existing verified email in place. */
export async function startEmailVerification(newEmail: string): Promise<UpdateEmailResponse> {
  const resp = await fetch(`${API_URL}/user/set_email`, {
    credentials: 'include',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ new_email: newEmail }),
  });

  if (!resp.ok) {
    throw new Error(`Error updating email: ${resp.statusText}`);
  }

  return resp.json();
}

/** Requests a fresh verification email for the current pending email address. */
export async function resendEmailVerification(): Promise<ResendEmailVerificationResponse> {
  const resp = await fetch(`${API_URL}/user/resend_email_verification`, {
    credentials: 'include',
    method: 'POST',
  });

  if (!resp.ok) {
    throw new Error(`Error resending verification email: ${resp.statusText}`);
  }

  return resp.json();
}

/** Abandons the current pending email verification for the signed-in user. */
export async function stopEmailVerification(): Promise<StopEmailVerificationResponse> {
  const resp = await fetch(`${API_URL}/user/stop_email_verification`, {
    credentials: 'include',
    method: 'POST',
  });

  if (!resp.ok) {
    throw new Error(`Error stopping email verification: ${resp.statusText}`);
  }

  return resp.json();
}

/** Payload used for both password-based and OAuth-based registration requests. */
export interface RegisterUserRequest {
  invite_code: string;
  username: string;
  fullname: string;
  email: string;
  email2: string;
  password?: string;
  password2?: string;
  oauth_identity?: OAuthIdentityInfo;
}

/** Registers a new user and returns the backend's status-discriminated outcome. */
export async function registerUser(
  registrationData: RegisterUserRequest
): Promise<RegistrationResponse> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // Important for session cookies
      body: JSON.stringify(registrationData),
    });
  } catch {
    throw new Error('A network error occurred');
  }

  if (!res.ok) {
    const errorEnvelope = await parseApiErrorResponse(res);
    throw new Error(apiErrorMessage({ operation: 'registration' }, res.status, errorEnvelope));
  }

  let responseData: unknown;
  try {
    responseData = await res.json();
  } catch {
    throw new Error('The server returned an invalid response');
  }

  return responseData as RegistrationResponse;
}

// =============================================================================
// Integration Health Dashboard types
// IMPORTANT: Keep in sync with rust/server/src/api/health_types.rs
// =============================================================================

// IMPORTANT: Keep in sync with HealthStatus in rust/server/src/api/health_types.rs
export enum HealthStatus {
  Healthy = 'healthy',
  Degraded = 'degraded',
  Unhealthy = 'unhealthy',
}

// IMPORTANT: Keep in sync with HealthEvent in rust/server/src/api/health_types.rs
export enum HealthEvent {
  Ok = 'ok',
  Timeout = 'timeout',
  Network = 'network',
  ParseFail = 'parse_fail',
  RateLimited = 'rate_limited',
  Auth = 'auth',
  Server5xx = 'server5xx',
  Client4xx = 'client4xx',
  Other = 'other',
}

// IMPORTANT: Keep in sync with TimestampedHealthEvent in rust/server/src/api/health_types.rs
interface TimestampedHealthEvent {
  time: string;
  event: HealthEvent;
}

// IMPORTANT: Keep in sync with DisplayClientHealthReport.metrics in rust/server/src/api/health_types.rs
export interface HealthMetrics {
  status: HealthStatus;
  total_events: number;
  error_events: number;
  error_rate: number;
  is_blocked: boolean;
  recent_events: TimestampedHealthEvent[];
}

// IMPORTANT: Keep in sync with DisplayClientHealthReport in rust/server/src/api/health_types.rs
export interface ClientHealthReport {
  provider: string;
  metrics: HealthMetrics;
}

// IMPORTANT: Keep in sync with HealthTotals in rust/server/src/api/health_types.rs
export interface HealthTotals {
  total_clients: number;
  healthy: number;
  degraded: number;
  unhealthy: number;
}

// IMPORTANT: Keep in sync with DisplayRoutingEntry in rust/server/src/api/health_types.rs
export interface RoutingEntry {
  provider: string;
  status: HealthStatus;
}

// IMPORTANT: Keep in sync with DisplayCatalogRouting in rust/server/src/api/health_types.rs
export interface CatalogRouting {
  catalog: string;
  active_providers: RoutingEntry[];
  excluded_providers: RoutingEntry[];
}

// IMPORTANT: Keep in sync with DisplayMarketplaceRouting in rust/server/src/api/health_types.rs
export interface MarketplaceRouting {
  marketplace: string;
  active_providers: RoutingEntry[];
  excluded_providers: RoutingEntry[];
  unavailable: boolean;
}

// IMPORTANT: Keep in sync with DisplayOrchestratorHealth in rust/server/src/api/health_types.rs
export interface OrchestratorHealth {
  orchestrator: string;
  totals: HealthTotals;
  clients: ClientHealthReport[];
  catalog_routing?: CatalogRouting[];
  marketplace_routing?: MarketplaceRouting[];
}

// IMPORTANT: Keep in sync with IntegrationHealthReport in rust/server/src/api/structs.rs
export interface IntegrationHealthReport {
  generated_at: string;
  fetch?: OrchestratorHealth;
  search?: OrchestratorHealth;
}
